import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiCalendar, FiZap, FiCheckCircle, FiAlertTriangle,FiEdit2 ,FiLayers, FiSettings, FiExternalLink } from 'react-icons/fi';
import { FaShopify } from 'react-icons/fa';
import { FiTag, FiImage, FiClock, FiSmartphone, FiCode } from 'react-icons/fi';
import { FaSearch, FaChartLine, FaLink } from 'react-icons/fa';
import {   FiLink, FiChevronDown, FiChevronUp, FiCheck, FiX } from 'react-icons/fi';


export default function SEODashboard(){
  const [activeTab, setActiveTab] = useState('critical');
  const [scanInProgress, setScanInProgress] = useState(false);
  const [scanTime, setScanTime] = useState('2 min 45 sec');
  const [issues, setIssues] = useState({
    critical: [
      {
        id: 1,
        type: 'broken-link',
        title: 'Broken links detected',
        count: 8,
        description: 'Links returning 404 errors that hurt user experience and SEO',
        examples: [
          { url: '/products/discontinued-product', status: 404, source: '/collections/all' },
          { url: '/old-blog-post', status: 404, source: '/blogs/news' }
        ],
        fixed: false
      },
      {
        id: 2,
        type: 'missing-meta',
        title: 'Missing meta titles',
        count: 12,
        description: 'Pages without title tags that reduce click-through rates',
        examples: [
          { url: '/products/new-arrival', field: 'title' },
          { url: '/collections/summer-collection', field: 'title' }
        ],
        fixed: false
      }
    ],
    warnings: [
      {
        id: 3,
        type: 'duplicate-meta',
        title: 'Duplicate meta descriptions',
        count: 5,
        description: 'Multiple pages sharing identical meta descriptions',
        examples: [
          { url: '/products/product-variant-1', content: 'Check out our amazing product' },
          { url: '/products/product-variant-2', content: 'Check out our amazing product' }
        ],
        fixed: false
      },
      {
        id: 4,
        type: 'missing-alt',
        title: 'Images without alt text',
        count: 23,
        description: 'Images missing alternative text for accessibility and SEO',
        examples: [
          { url: '/products/popular-item', image: 'product-image-1.jpg' },
          { url: '/blogs/fashion-tips', image: 'blog-hero.jpg' }
        ],
        fixed: false
      }
    ],
    optimized: [
      {
        id: 5,
        type: 'schema',
        title: 'Structured data implemented',
        count: 42,
        description: 'Pages with proper Schema.org markup',
        examples: [],
        fixed: true
      }
    ]
  });

  const [performanceData, setPerformanceData] = useState({
    mobileScore: 78,
    desktopScore: 92,
    slowPages: [
      { url: '/products/complex-product', loadTime: '4.8s' },
      { url: '/blogs/comprehensive-guide', loadTime: '5.2s' }
    ]
  });

  const startNewScan = () => {
    setScanInProgress(true);
    setTimeout(() => {
      setScanInProgress(false);
      // In a real app, you would update issues from an API call here
    }, 5000);
  };

  const fixIssue = (priority, id) => {
    setIssues(prev => ({
      ...prev,
      [priority]: prev[priority].map(issue => 
        issue.id === id ? { ...issue, fixed: true } : issue
      )
    }));
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'warnings': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'critical': return <FiAlertTriangle className="mr-2" />;
      case 'warnings': return <FiInfo className="mr-2" />;
      default: return <FiCheckCircle className="mr-2" />;
    }
  };

  const getIssueIcon = (type) => {
    switch(type) {
      case 'broken-link': return <FaLink className="mr-2" />;
      case 'missing-meta': return <FaSearch className="mr-2" />;
      case 'duplicate-meta': return <FaSearch className="mr-2" />;
      case 'missing-alt': return <FiSmartphone className="mr-2" />;
      case 'slow-page': return <FiClock className="mr-2" />;
      case 'schema': return <FiCode className="mr-2" />;
      default: return <FaChartLine className="mr-2" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            <span className="bg-gradient-to-r from-blue-800 to-teal-500 bg-clip-text text-transparent">
              SEO Issue Detection
            </span>
          </h1>
          <div className="flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <FiClock className="mr-1" /> Last scan: {new Date().toLocaleDateString()}
            </span>
            <button
              onClick={startNewScan}
              disabled={scanInProgress}
              className={`px-4 py-2 rounded-md font-medium ${scanInProgress 
                ? 'bg-gray-300 text-gray-600' 
                : 'bg-gradient-to-r from-blue-800 to-teal-500 text-white hover:opacity-90'
              } transition shadow-md`}
            >
              {scanInProgress ? 'Scanning...' : 'Run New Scan'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">SEO Health Score</h3>
            <div className="flex items-center">
              <div className="w-full bg-gray-200 rounded-full h-4 mr-4">
                <div 
                  className="bg-gradient-to-r from-blue-800 to-teal-500 h-4 rounded-full" 
                  style={{ width: '68%' }}
                ></div>
              </div>
              <span className="text-2xl font-bold">68/100</span>
            </div>
            <p className="mt-2 text-sm text-gray-600">+12% improvement from last scan</p>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Performance</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center">
                  <FiSmartphone className="text-blue-800 mr-2" />
                  <span>Mobile:</span>
                </div>
                <span className="text-2xl font-bold">{performanceData.mobileScore}/100</span>
              </div>
              <div>
                <div className="flex items-center">
                  <FiCode className="text-teal-500 mr-2" />
                  <span>Desktop:</span>
                </div>
                <span className="text-2xl font-bold">{performanceData.desktopScore}/100</span>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Scan Details</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Pages scanned:</span>
                <span className="font-medium">142</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Scan time:</span>
                <span className="font-medium">{scanTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Issues found:</span>
                <span className="font-medium">48</span>
              </div>
            </div>
          </div>
        </div>

        {/* Issues Tabs */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('critical')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'critical' 
                  ? 'border-red-500 text-red-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Critical Issues ({issues.critical.length})
              </button>
              <button
                onClick={() => setActiveTab('warnings')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'warnings' 
                  ? 'border-yellow-500 text-yellow-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Warnings ({issues.warnings.length})
              </button>
              <button
                onClick={() => setActiveTab('optimized')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'optimized' 
                  ? 'border-green-500 text-green-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Optimized ({issues.optimized.length})
              </button>
            </nav>
          </div>

          {/* Issues List */}
          <div className="divide-y divide-gray-200">
            {issues[activeTab].map((issue) => (
              <div key={issue.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(activeTab)}`}>
                        {getPriorityIcon(activeTab)}
                        {issue.title}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">{issue.count} occurrences</span>
                    </div>
                    <p className="text-gray-600">{issue.description}</p>
                    
                    {issue.examples.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Examples:</h4>
                        <ul className="space-y-2">
                          {issue.examples.slice(0, 2).map((example, idx) => (
                            <li key={idx} className="flex items-center text-sm text-gray-600">
                              {getIssueIcon(issue.type)}
                              {example.url && (
                                <>
                                  <a href={example.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                                    {example.url} {example.status && `(Status: ${example.status})`}
                                    <FiExternalLink className="ml-1" size={12} />
                                  </a>
                                  {example.source && (
                                    <span className="text-gray-500 ml-2">← from {example.source}</span>
                                  )}
                                </>
                              )}
                              {example.image && (
                                <span>Image: {example.image}</span>
                              )}
                              {example.content && (
                                <span>Content: "{example.content}"</span>
                              )}
                            </li>
                          ))}
                        </ul>
                        {issue.examples.length > 2 && (
                          <p className="text-sm text-gray-500 mt-1">+ {issue.examples.length - 2} more examples</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {!issue.fixed && activeTab !== 'optimized' && (
                    <button
                      onClick={() => fixIssue(activeTab, issue.id)}
                      className="ml-4 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-800 to-teal-500 hover:opacity-90 shadow-sm"
                    >
                      Fix Issue
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Section */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Performance Issues</h3>
          </div>
          <div className="p-6">
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">Slow-Loading Pages</h4>
              <div className="space-y-4">
                {performanceData.slowPages.map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiClock className="text-yellow-500 mr-2" />
                      <a href={page.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {page.url}
                      </a>
                    </div>
                    <span className="text-red-600 font-medium">{page.loadTime} load time</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Mobile Responsiveness</h4>
              <div className="flex items-center">
                <div className="w-full bg-gray-200 rounded-full h-2.5 mr-4">
                  <div 
                    className="bg-gradient-to-r from-blue-800 to-teal-500 h-2.5 rounded-full" 
                    style={{ width: `${performanceData.mobileScore}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600">{performanceData.mobileScore}/100 Google Mobile Score</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Issues detected: Text too small, Clickable elements too close, Content wider than screen
              </p>
            </div>
          </div>
        </div>

        {/* Structured Data Section */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Structured Data (Schema Markup)</h3>
          </div>
          <div className="p-6">
            <div className="flex items-start">
              <div className={`p-3 rounded-lg ${issues.optimized[0].fixed ? 'bg-green-50' : 'bg-yellow-50'}`}>
                {issues.optimized[0].fixed ? (
                  <FiCheckCircle className="text-green-500 text-2xl" />
                ) : (
                  <FiAlertTriangle className="text-yellow-500 text-2xl" />
                )}
              </div>
              <div className="ml-4">
                <h4 className="text-md font-medium text-gray-900">
                  {issues.optimized[0].fixed ? 'Schema markup implemented correctly' : 'Missing structured data'}
                </h4>
                <p className="text-gray-600 mt-1">
                  {issues.optimized[0].fixed 
                    ? `Your store has ${issues.optimized[0].count} pages with proper Schema.org markup`
                    : 'Add structured data to improve rich snippets in search results'}
                </p>
                {!issues.optimized[0].fixed && (
                  <button className="mt-3 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-800 to-teal-500 hover:opacity-90 shadow-sm">
                    Generate Schema Markup
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
